import { forwardRef, useState, useRef } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string
  error?: string
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  helperText?: string
  variant?: 'default' | 'premium' | 'glass' | 'floating' | 'minimal'
  size?: 'sm' | 'md' | 'lg'
  floatingLabel?: boolean
}

const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  leftIcon,
  rightIcon,
  helperText,
  variant = 'default',
  size = 'md',
  floatingLabel = false,
  className = '',
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = useState(false)
  const [hasValue, setHasValue] = useState(!!props.value || !!props.defaultValue)
  const internalRef = useRef<HTMLInputElement>(null)
  const inputRef = ref || internalRef

  // Generate unique IDs for accessibility
  const inputId = props.id || `input-${Math.random().toString(36).substr(2, 9)}`
  const errorId = `${inputId}-error`
  const helperId = `${inputId}-helper`
  
  const hasError = !!error
  
  const baseClasses = 'w-full transition-all duration-300 focus:outline-none'
  
  const variantClasses = {
    default: 'rounded-2xl border bg-white focus:ring-2 focus:ring-offset-1',
    premium: 'rounded-2xl border bg-gradient-to-r from-white to-gray-50/50 focus:ring-2 focus:ring-offset-1 shadow-premium-sm focus:shadow-premium-md',
    glass: 'rounded-2xl border bg-white/20 backdrop-blur-2xl focus:bg-white/30 border-white/20 focus:border-white/40 shadow-glass-shadow',
    floating: 'rounded-2xl border-0 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-offset-1 shadow-premium-sm focus:shadow-premium-md',
    minimal: 'rounded-lg border-0 border-b-2 bg-transparent focus:bg-gray-50/50 rounded-b-none'
  }
  
  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-base',
    lg: 'px-6 py-4 text-lg'
  }
  
  const colorClasses = hasError 
    ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20 text-red-900' 
    : variant === 'glass'
    ? 'border-white/20 focus:border-primary-400/50 focus:ring-primary-500/20 text-gray-900'
    : variant === 'minimal'
    ? 'border-gray-200 focus:border-primary-500 text-gray-900'
    : 'border-gray-200 focus:border-primary-500 focus:ring-primary-500/20 text-gray-900'
  
  const iconPadding = {
    sm: leftIcon ? 'pl-9' : rightIcon ? 'pr-9' : '',
    md: leftIcon ? 'pl-12' : rightIcon ? 'pr-12' : '',
    lg: leftIcon ? 'pl-14' : rightIcon ? 'pr-14' : ''
  }

  const disabledClasses = props.disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
  
  const inputClasses = [
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    colorClasses,
    iconPadding[size],
    disabledClasses,
    floatingLabel && 'placeholder-transparent',
    className
  ].filter(Boolean).join(' ')

  const handleFocus = () => {
    setIsFocused(true)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)
    setHasValue(!!e.target.value)
    props.onBlur?.(e)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasValue(!!e.target.value)
    props.onChange?.(e)
  }

  const labelFloating = floatingLabel && (isFocused || hasValue)

  return (
    <div className="space-y-2">
      <div className="relative">
        {/* Standard Label */}
        {label && !floatingLabel && (
          <motion.label
            htmlFor={inputId}
            className="block text-sm font-semibold text-gray-700 mb-2"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            {label}
          </motion.label>
        )}
        
        {/* Input Container */}
        <div className="relative">
          {/* Left Icon */}
          {leftIcon && (
            <motion.div 
              className={`absolute inset-y-0 left-0 ${size === 'sm' ? 'pl-2.5' : size === 'lg' ? 'pl-4' : 'pl-3'} flex items-center pointer-events-none`}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <span className={`${hasError ? 'text-red-400' : isFocused ? 'text-primary-500' : 'text-gray-400'} transition-colors duration-200`}>
                {leftIcon}
              </span>
            </motion.div>
          )}
          
          {/* Input Field */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <input
              ref={inputRef}
              id={inputId}
              className={inputClasses}
              aria-describedby={[
                error ? errorId : '',
                helperText ? helperId : ''
              ].filter(Boolean).join(' ') || undefined}
              aria-invalid={hasError}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={handleChange}
              {...props}
            />
          </motion.div>
          
          {/* Floating Label */}
          {label && floatingLabel && (
            <motion.label
              htmlFor={inputId}
              className={`absolute left-4 pointer-events-none transition-all duration-200 ${
                hasError ? 'text-red-500' : isFocused ? 'text-primary-600' : 'text-gray-500'
              } ${
                leftIcon ? (size === 'sm' ? 'left-9' : size === 'lg' ? 'left-14' : 'left-12') : 'left-4'
              }`}
              animate={{
                top: labelFloating ? (size === 'sm' ? '-8px' : size === 'lg' ? '-12px' : '-10px') : '50%',
                fontSize: labelFloating ? (size === 'lg' ? '14px' : '12px') : size === 'lg' ? '18px' : '16px',
                y: labelFloating ? 0 : '-50%',
                fontWeight: labelFloating ? 600 : 400
              }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              <span className={`px-1 ${labelFloating ? 'bg-white rounded' : ''}`}>
                {label}
              </span>
            </motion.label>
          )}
          
          {/* Right Icon */}
          {rightIcon && (
            <motion.div 
              className={`absolute inset-y-0 right-0 ${size === 'sm' ? 'pr-2.5' : size === 'lg' ? 'pr-4' : 'pr-3'} flex items-center`}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 }}
            >
              <span className={`${hasError ? 'text-red-400' : isFocused ? 'text-primary-500' : 'text-gray-400'} transition-colors duration-200`}>
                {rightIcon}
              </span>
            </motion.div>
          )}

          {/* Focus Ring Enhancement */}
          <AnimatePresence>
            {isFocused && variant !== 'minimal' && (
              <motion.div
                className={`absolute inset-0 rounded-2xl border-2 pointer-events-none ${
                  hasError ? 'border-red-500' : 'border-primary-500'
                }`}
                initial={{ opacity: 0, scale: 1.02 }}
                animate={{ opacity: 0.3, scale: 1 }}
                exit={{ opacity: 0, scale: 1.02 }}
                transition={{ duration: 0.2 }}
              />
            )}
          </AnimatePresence>
        </div>
      </div>
      
      {/* Error Message */}
      <AnimatePresence>
        {error && (
          <motion.p
            id={errorId}
            className="text-sm text-red-600 flex items-center gap-1"
            role="alert"
            aria-live="polite"
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </motion.p>
        )}
      </AnimatePresence>
      
      {/* Helper Text */}
      {helperText && !error && (
        <motion.p
          id={helperId}
          className="text-sm text-gray-500 flex items-center gap-1"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          {helperText}
        </motion.p>
      )}
    </div>
  )
})

Input.displayName = 'Input'

export default Input